import {FC, useEffect, useRef, useState, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {
  createChart,
  ColorType,
  LineStyle,
  IChartApi,
  ISeriesApi,
  LineWidth,
  Time,
} from 'lightweight-charts'
import {KTIcon} from '../../../../_metronic/helpers'
import {useAuth} from '../../../modules/auth/core/Auth'

type Candle = {time: number; open: number; high: number; low: number; close: number}
type CandleResponse = {candles: Candle[]; lastUpdate?: number}

interface TradingViewLightChartProps {
  symbol?: string
  interval?: string
  height?: number
  entryPrice?: number
  stopLoss?: number
  takeProfits?: number[]
  market?: 'crypto' | 'forex' | 'index' | 'stock' | 'commodity' | 'etf'
  highlightBoxes?: {
    low: number
    high: number
    fromTime?: number | string | Date
    toTime?: number | string | Date
    color?: string
    opacity?: number
    borderColor?: string
    label?: string
  }[]
  showPriceLines?: boolean
  timezone?: string
}

const API_BASE_URL =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/$/, '')) || '/api'

/** Number of candles for chart history */
const CHART_HISTORY_LIMIT = 600

const TIMEZONE_OFFSETS: { [key: string]: number } = {
  'UTC': 0,
  'UTC-12': -12 * 3600,
  'UTC-11': -11 * 3600,
  'UTC-10': -10 * 3600,
  'UTC-9': -9 * 3600,
  'UTC-8': -8 * 3600,
  'UTC-7': -7 * 3600,
  'UTC-6': -6 * 3600,
  'UTC-5': -5 * 3600,
  'UTC-4': -4 * 3600,
  'UTC-3': -3 * 3600,
  'UTC-2': -2 * 3600,
  'UTC-1': -1 * 3600,
  'UTC+0': 0,
  'UTC+1': 1 * 3600,
  'UTC+2': 2 * 3600,
  'UTC+3': 3 * 3600,
  'UTC+3:30': 3.5 * 3600,
  'UTC+4': 4 * 3600,
  'UTC+5': 5 * 3600,
  'UTC+5:30': 5.5 * 3600,
  'UTC+5:45': 5.75 * 3600,
  'UTC+6': 6 * 3600,
  'UTC+6:30': 6.5 * 3600,
  'UTC+7': 7 * 3600,
  'UTC+8': 8 * 3600,
  'UTC+9': 9 * 3600,
  'UTC+9:30': 9.5 * 3600,
  'UTC+10': 10 * 3600,
  'UTC+10:30': 10.5 * 3600,
  'UTC+11': 11 * 3600,
  'UTC+12': 12 * 3600,
  'UTC+12:45': 12.75 * 3600,
  'UTC+13': 13 * 3600,
  'UTC+13:45': 13.75 * 3600,
  'UTC+14': 14 * 3600,
}

const TIMEZONE_VALUES = [
  'UTC', 'UTC-12', 'UTC-11', 'UTC-10', 'UTC-9', 'UTC-8', 'UTC-7', 'UTC-6', 'UTC-5', 'UTC-4',
  'UTC-3', 'UTC-2', 'UTC-1', 'UTC+0', 'UTC+1', 'UTC+2', 'UTC+3', 'UTC+3:30', 'UTC+4', 'UTC+5',
  'UTC+5:30', 'UTC+5:45', 'UTC+6', 'UTC+6:30', 'UTC+7', 'UTC+8', 'UTC+9', 'UTC+9:30', 'UTC+10',
  'UTC+10:30', 'UTC+11', 'UTC+12', 'UTC+12:45', 'UTC+13', 'UTC+13:45', 'UTC+14',
]

const getTzLabelKey = (value: string): string => {
  const part = value.replace(/-/g, '_MINUS_').replace(/\+/g, '_PLUS_').replace(/:/g, '_')
  return `CHART.TZ.${part || 'UTC'}`
}

const canUserSeeLabels = (showLabelValue: number | string | boolean | undefined): boolean => {
  return showLabelValue === 1 || showLabelValue === '1' || showLabelValue === true
}

const toEpochSeconds = (t: Time): number => {
  if (typeof t === 'number') {
    return Math.floor(t)
  }
  if (t && typeof t === 'object' && 'year' in t && typeof t.year === 'number') {
    const month = (t.month !== undefined && t.month >= 1 && t.month <= 12) ? t.month - 1 : 0
    const day = (t.day !== undefined && t.day >= 1 && t.day <= 31) ? t.day : 1
    const d = new Date(Date.UTC(t.year, month, day, 0, 0, 0))
    return Math.floor(d.getTime() / 1000)
  }
  const num = Number(t)
  return isNaN(num) ? 0 : Math.floor(num)
}

const intervalToMinutes = (interval?: string): number => {
  if (!interval) return 60
  const iv = interval.toLowerCase()
  if (iv === '1') return 1
  if (iv === '5') return 5
  if (iv === '15') return 15
  if (iv === '30') return 30
  if (iv === '60' || iv === '1h') return 60
  if (iv === '240' || iv === '4h') return 240
  if (iv === '1d' || iv === 'd') return 1440
  if (iv === '3d') return 4320
  if (iv === '1w' || iv === 'w') return 10080
  if (iv === '1m' || iv === '1M' || iv === 'm') return 43200
  return 60
}

const formatTimeForTimezone = (ts: number, tz: string, interval?: string): string => {
  const offset = TIMEZONE_OFFSETS[tz] || 0
  const timestampMs = ts * 1000 + offset * 1000
  const d = new Date(timestampMs)
  
  const year = d.getUTCFullYear()
  const month = d.getUTCMonth() + 1
  const day = d.getUTCDate()
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  
  const intervalMinutes = intervalToMinutes(interval)
  
  const englishMonths = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]
  
  if (intervalMinutes < 30) {
    return `${hh}:${mm}`
  } else if (intervalMinutes < 1440) {
    return String(day)
  } else if (intervalMinutes === 1440) {
    return englishMonths[month - 1] || String(month)
  } else {
    return String(year)
  }
}

/** Always returns the exact date and time for display in crosshair (hover over candle) */
const formatTimeForCrosshair = (ts: number, tz: string): string => {
  const offset = TIMEZONE_OFFSETS[tz] || 0
  const timestampMs = ts * 1000 + offset * 1000
  const d = new Date(timestampMs)
  const year = d.getUTCFullYear()
  const month = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${year}/${month}/${day} ${hh}:${mm}`
}


function parseTimeInChartTimezone(t: unknown, _chartTimezone: string): number | undefined {
  if (t === undefined || t === null) return undefined
  if (typeof t === 'number') return Math.floor(t)
  if (t instanceof Date) {
    const ms = t.getTime()
    if (Number.isNaN(ms)) return undefined
    return Math.floor(ms / 1000)
  }
  if (typeof t !== 'string') return undefined
  const s = t.trim()
  if (!s) return undefined
  // Explicit UTC (Z or +00:00) → use as UTC
  if (/Z\s*$/i.test(s) || /[+-]00:00\s*$/.test(s)) {
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return undefined
    return Math.floor(d.getTime() / 1000)
  }
  // Parse as "YYYY-MM-DD HH:mm" or "YYYY-MM-DDTHH:mm" or "YYYY/MM/DD"
  // API/admin store datetimes in UTC; interpret as UTC so chart (candles in UTC) shows correct range
  const withTime = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[T\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?\s*$/)
  const dateOnly = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s*$/)
  const match = withTime || dateOnly
  if (match) {
    const y = parseInt(match[1], 10)
    const m = parseInt(match[2], 10) - 1
    const day = parseInt(match[3], 10)
    const h = withTime ? parseInt(match[4], 10) || 0 : 0
    const min = withTime ? parseInt(match[5], 10) || 0 : 0
    const sec = withTime && match[6] !== undefined ? parseInt(match[6], 10) || 0 : 0
    const utcMs = Date.UTC(y, m, day, h, min, sec, 0)
    return Math.floor(utcMs / 1000)
  }
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return undefined
  return Math.floor(d.getTime() / 1000)
}

const intervalToBinance = (interval?: string): string => {
  if (!interval) return '1h'
  if (interval.match(/^(60|1h)$/i)) return '1h'
  if (interval.match(/^(30)$/)) return '30m'
  if (interval.match(/^(15)$/)) return '15m'
  if (interval.match(/^(240|4h)$/i)) return '4h'
  if (interval.match(/^(1d|D)$/i)) return '1d'
  if (interval.match(/^(3d)$/i)) return '3d'
  if (interval.match(/^(1w)$/i)) return '1w'
  if (interval.match(/^(1M)$/i)) return '1M'
  return '1h'
}

async function fetchCryptoCandles(symbol: string, interval: string): Promise<CandleResponse> {
  const cleanedSymbol = symbol.replace(/^BINANCE:/i, '') || 'BTCUSDT'
  const binanceInterval = intervalToBinance(interval)
  const url = `${API_BASE_URL}/crypto-candles?symbol=${encodeURIComponent(
    cleanedSymbol.toUpperCase()
  )}&interval=${encodeURIComponent(binanceInterval)}&limit=${CHART_HISTORY_LIMIT}`
  const response = await fetch(url)
  if (!response.ok) {
    const msg = (await response.json().catch(() => null))?.message
    throw new Error(msg || 'Error fetching data from server')
  }
  const data = await response.json()
  const raw = Array.isArray(data?.candles) ? data.candles : Array.isArray(data) ? data : []
  const mapped: Candle[] = raw.map((c: any) => {
    if (Array.isArray(c)) {
      return {
        time: Math.floor(Number(c[0]) / 1000),
        open: Number(c[1]),
        high: Number(c[2]),
        low: Number(c[3]),
        close: Number(c[4]),
      }
    } else {
      return {
        time: Number(c.time),
        open: Number(c.open),
        high: Number(c.high),
        low: Number(c.low),
        close: Number(c.close),
      }
    }
  })
  mapped.sort((a: Candle, b: Candle) => a.time - b.time)
  const unique: Candle[] = []
  let lastTime: number | null = null
  for (const item of mapped) {
    if (lastTime === null || item.time !== lastTime) {
      unique.push(item)
      lastTime = item.time
    } else {
      unique[unique.length - 1] = item
    }
  }
  return {
    candles: unique,
    lastUpdate: typeof data?.lastUpdate === 'number' ? data.lastUpdate : undefined,
  }
}

async function fetchForexCandles(symbol: string, interval: string, market?: string): Promise<CandleResponse> {
  const cleanedSymbol = symbol.replace('/', '').toUpperCase() || 'EURUSD'
  const params = new URLSearchParams({
    symbol: cleanedSymbol,
    interval,
    limit: String(CHART_HISTORY_LIMIT),
  })
  if (market) params.set('market', market)
  const url = `${API_BASE_URL}/forex-candles?${params.toString()}`
  const response = await fetch(url)
  if (!response.ok) {
    const data = await response.json().catch(() => null)
    const msg = data?.message
    throw new Error(msg || 'Error fetching data from server')
  }
  const data = await response.json()
  const candles = (Array.isArray(data?.candles) ? data.candles : []).map((c: any) => ({
    time: Number(c.time),
    open: Number(c.open),
    high: Number(c.high),
    low: Number(c.low),
    close: Number(c.close),
  }))
  return {
    candles,
    lastUpdate: typeof data?.lastUpdate === 'number' ? data.lastUpdate : undefined,
  }
}

export const TradingViewLightChart: FC<TradingViewLightChartProps> = ({
  symbol = 'BTCUSDT',
  interval = '60',
  height = 480,
  entryPrice,
  stopLoss,
  takeProfits = [],
  market,
  highlightBoxes = [],
  showPriceLines = true,
  timezone = 'UTC+3:30',
}) => {
  const {currentUser} = useAuth()
  const intl = useIntl()
  const chartContainerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const priceLineRefs = useRef<any[]>([])
  const boxRefs = useRef<HTMLDivElement[]>([])
  const boxLabelRefs = useRef<(HTMLDivElement | null)[]>([])
  const candlesRef = useRef<any[]>([])
  const lastTimestampRef = useRef<number | null>(null)
  const originalLastTimestampRef = useRef<number | null>(null)
  const lastUpdateRef = useRef<number | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const initialTimeRangeRef = useRef<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const defaultTzByLocale =
    intl.locale?.toLowerCase().startsWith('en') ? 'UTC-5' : timezone
  const [selectedTimezone, setSelectedTimezone] = useState<string>(
    TIMEZONE_OFFSETS[defaultTzByLocale] !== undefined ? defaultTzByLocale : 'UTC'
  )

  const canShowLabels = useMemo(
    () => canUserSeeLabels(currentUser?.show_label),
    [currentUser?.show_label]
  )

  useEffect(() => {
    if (TIMEZONE_OFFSETS[selectedTimezone] === undefined) {
      setSelectedTimezone('UTC')
    }
  }, [selectedTimezone])

  useEffect(() => {
    let active = true
    setError(undefined)
    setLoading(true)

    const run = async () => {
      let candles: any[] = []
      try {
        if (!market) {
          throw new Error(intl.formatMessage({id: 'CHART.ERROR_MARKET_REQUIRED'}))
        }

        if (market === 'crypto') {
          const validCryptoIntervals = ['15', '30', '60', '240', '1d', '3d', '1w', '1M']
          if (!validCryptoIntervals.includes(interval)) {
            throw new Error(intl.formatMessage({id: 'CHART.ERROR_INVALID_TIMEFRAME'}))
          }
          const res = await fetchCryptoCandles(symbol.replace('BINANCE:', ''), interval)
          candles = res.candles
          lastUpdateRef.current = typeof res.lastUpdate === 'number' ? res.lastUpdate : null
        } else if (
          market === 'forex' ||
          market === 'index' ||
          market === 'stock' ||
          market === 'commodity' ||
          market === 'etf'
        ) {
          const res = await fetchForexCandles(symbol, interval, market)
          candles = res.candles
          lastUpdateRef.current = typeof res.lastUpdate === 'number' ? res.lastUpdate : null
        } else {
          throw new Error(
            intl.formatMessage({id: 'CHART.ERROR_MARKET_UNSUPPORTED'}, {market: String(market)})
          )
        }
      } catch (e: any) {
        if (active) setError(e.message || intl.formatMessage({id: 'CHART.ERROR_FETCH_CANDLES'}))
        candles = []
      }
      if (active && candlestickSeriesRef.current) {
        candlestickSeriesRef.current.setData(candles)
        candlesRef.current = candles
        lastTimestampRef.current = candles.length > 0 ? candles[candles.length - 1].time : null
        originalLastTimestampRef.current = candles.length > 0 ? candles[candles.length - 1].time : null
        lastUpdateRef.current = Date.now() / 1000

        if (chartRef.current && candles.length > 0) {
          setTimeout(() => {
            if (chartRef.current) {
              const timeRange = chartRef.current.timeScale().getVisibleRange()
              initialTimeRangeRef.current = timeRange
            }
          }, 100)
        }

        setLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [symbol, interval, market, selectedTimezone, intl])

  const getPollingInterval = (interval: string, market?: string): number => {
    const oandaLike = market === 'crypto' || market === 'forex' || market === 'index' || market === 'stock' || market === 'commodity' || market === 'etf'
    if (oandaLike) {
      const iv = interval.toLowerCase()
      if (['1d', '3d', '1w', '1m'].includes(iv)) return 35
      if (iv === '240' || iv === '4h') return 25
      if (iv === '60' || iv === '1h') return 18
      if (iv === '30' || iv === '15') return 12
      return 12
    }

    const iv = interval.toLowerCase()
    if (iv === '1') return 4
    if (iv === '5') return 8
    if (iv === '15') return 18
    if (iv === '30') return 35
    if (iv === '60') return 40
    if (iv === '240') return 60
    if (['1d', '3d', '1w', '1M'].includes(iv)) return 300
    return 300
  }

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    const hasEnoughData = candlesRef.current.length >= CHART_HISTORY_LIMIT / 3
    const isReady = lastTimestampRef.current && !error && !loading && hasEnoughData

    if (!isReady) {
      return
    }

    const poll = async () => {
      try {
        const url = `${API_BASE_URL}/realtime-candles?symbol=${encodeURIComponent(
          symbol
        )}&interval=${encodeURIComponent(interval)}&market=${encodeURIComponent(
          market || 'forex'
        )}&since=${originalLastTimestampRef.current}`
        const response = await fetch(url)
        if (!response.ok) return
        const data = await response.json()
        const backendLastUpdate = typeof data?.lastUpdate === 'number' ? data.lastUpdate : null

        if (
          backendLastUpdate !== null &&
          lastUpdateRef.current !== null &&
          backendLastUpdate <= lastUpdateRef.current
        ) {
          return
        }

        const newCandles = Array.isArray(data?.candles) ? data.candles : []
        if (newCandles.length > 0 && candlestickSeriesRef.current) {
          newCandles.forEach((candle: any) => {
            candlestickSeriesRef.current?.update(candle)

            const lastIdx = candlesRef.current.length - 1
            if (lastIdx >= 0 && candlesRef.current[lastIdx].time === candle.time) {
              candlesRef.current[lastIdx] = candle
            } else {
              candlesRef.current.push(candle)
            }
          })

          lastTimestampRef.current = candlesRef.current[candlesRef.current.length - 1].time
          lastUpdateRef.current = backendLastUpdate ?? Date.now() / 1000
        }
      } catch (e) {
      }
    }

    const intervalMs = getPollingInterval(interval, market) * 1000
    const firstPollTimeout = setTimeout(poll, 2000)
    const pollingTimeout = setTimeout(() => {
      pollingIntervalRef.current = setInterval(poll, intervalMs)
    }, 2000)

    return () => {
      clearTimeout(firstPollTimeout)
      clearTimeout(pollingTimeout)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [symbol, interval, market, loading, error, candlesRef.current.length])

  useEffect(() => {
    if (!chartContainerRef.current) return
    if (chartRef.current) {
      try {
        chartRef.current.remove()
      } catch {
      }
      chartRef.current = null
      candlestickSeriesRef.current = null
      priceLineRefs.current.forEach((pl) => {
        try {
          pl.remove()
        } catch {}
      })
      priceLineRefs.current = []
    }
    const chart = createChart(chartContainerRef.current, {
      height: height,
      width: chartContainerRef.current.offsetWidth,
      rightPriceScale: {scaleMargins: {top: 0.12, bottom: 0.18}, borderColor: '#D1D4DC'},
      layout: {
        background: {type: ColorType.Solid, color: '#ffffff'},
        textColor: '#1f2937',
      },
      grid: {
        horzLines: {visible: false},
        vertLines: {visible: false},
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#D1D4DC',
        rightOffset: 10,
        tickMarkFormatter: (t: Time) => formatTimeForTimezone(toEpochSeconds(t), selectedTimezone, interval),
      },
      localization: {
        timeFormatter: (t: Time) => formatTimeForCrosshair(toEpochSeconds(t), selectedTimezone),
      },
      crosshair: {
        mode: 0,
      },
    })
    chartRef.current = chart
    const candlestickSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: true,
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#1a7f73',
      wickDownColor: '#c63c3c',
      ...(market === 'forex' || market === 'index' || market === 'stock' || market === 'commodity' || market === 'etf'
        ? {
            priceFormat: {
              type: 'price',
              precision: 5,
              minMove: 0.00001,
            },
          }
        : {}),
    })
    candlestickSeriesRef.current = candlestickSeries
    const handleResize = () => {
      if (chart && chartContainerRef.current) {
        chart.applyOptions({width: chartContainerRef.current.offsetWidth})
        try {
          recalcBoxes()
        } catch {}
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [symbol, interval, height, market])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.applyOptions({
      timeScale: {
        tickMarkFormatter: (t: Time) => formatTimeForTimezone(toEpochSeconds(t), selectedTimezone, interval),
      },
      localization: {
        timeFormatter: (t: Time) => formatTimeForCrosshair(toEpochSeconds(t), selectedTimezone),
      },
    })
  }, [selectedTimezone, interval])

  useEffect(() => {
    const cs = candlestickSeriesRef.current
    if (!cs) return
    // Always clear previous signal's price lines when switching (same symbol or not)
    priceLineRefs.current.forEach((pl) => {
      try {
        pl.remove()
      } catch {}
    })
    priceLineRefs.current = []
    if (!showPriceLines) {
      return () => {
        priceLineRefs.current.forEach((pl) => {
          try {
            pl.remove()
          } catch {}
        })
        priceLineRefs.current = []
      }
    }
    if (typeof entryPrice === 'number' && !isNaN(entryPrice) && entryPrice > 0) {
      priceLineRefs.current.push(
        cs.createPriceLine({
          price: entryPrice,
          color: '#374151',
          lineWidth: 2 as LineWidth,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: 'Entry',
        })
      )
    }
    if (typeof stopLoss === 'number' && !isNaN(stopLoss) && stopLoss > 0) {
      priceLineRefs.current.push(
        cs.createPriceLine({
          price: stopLoss,
          color: '#ef4444',
          lineWidth: 2 as LineWidth,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: 'Stop',
        })
      )
    }
    takeProfits.forEach((tp, i) => {
      if (typeof tp === 'number' && !isNaN(tp) && tp > 0) {
        priceLineRefs.current.push(
          cs.createPriceLine({
            price: tp,
            color: '#22c55e',
            lineWidth: 2 as LineWidth,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: true,
            title: `TP${i + 1}`,
          })
        )
      }
    })
    return () => {
      priceLineRefs.current.forEach((pl) => {
        try {
          pl.remove()
        } catch {}
      })
      priceLineRefs.current = []
    }
  }, [entryPrice, stopLoss, JSON.stringify(takeProfits), symbol, interval, showPriceLines])

  useEffect(() => {
    const container = chartContainerRef.current
    if (!container) return

    // Cleanup: Remove all existing overlay boxes and labels from DOM
    // This ensures no leftover elements from previous signals (even with same symbol)
    boxRefs.current.forEach((box) => {
      if (box?.parentNode) {
        box.parentNode.removeChild(box)
      }
    })
    boxRefs.current = []
    
    boxLabelRefs.current.forEach((label) => {
      if (label?.parentNode) {
        label.parentNode.removeChild(label)
      }
    })
    boxLabelRefs.current = []
    
    const existingOverlays = container.querySelectorAll('[data-tvlc-overlay-box]')
    existingOverlays.forEach((el) => {
      el.parentNode?.removeChild(el)
    })
    
    const existingLabels = container.querySelectorAll('[data-tvlc-overlay-label]')
    existingLabels.forEach((el) => {
      el.parentNode?.removeChild(el)
    })

    // Check if user has permission to see labels (based on show_label field in database)
    // Using memoized value from component state

    highlightBoxes.forEach((box, index) => {
      if (!box || typeof box.low !== 'number' || typeof box.high !== 'number') return

      // Create highlight box element
      const div = document.createElement('div')
      div.setAttribute('data-tvlc-overlay-box', '1')
      div.style.position = 'absolute'
      div.style.pointerEvents = 'none'
      div.style.zIndex = '3'
      div.style.display = 'none'
      boxRefs.current[index] = div

      // Create label element only if:
      // 1. User has permission (show_label = 1)
      // 2. Label text exists and is not empty
      if (canShowLabels && box.label && String(box.label).trim().length > 0) {
        const labelDiv = document.createElement('div')
        labelDiv.setAttribute('data-tvlc-overlay-label', '1')
        labelDiv.style.position = 'absolute'
        labelDiv.style.top = '6px'
        labelDiv.style.left = '6px'
        labelDiv.style.padding = '4px 8px'
        labelDiv.style.borderRadius = '4px'
        labelDiv.style.fontSize = '12px'
        labelDiv.style.fontWeight = '600'
        labelDiv.style.background = 'rgba(255,255,255,0.95)'
        labelDiv.style.color = '#111827'
        labelDiv.style.display = 'none'
        labelDiv.style.pointerEvents = 'auto'
        labelDiv.style.zIndex = '100'
        labelDiv.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)'
        labelDiv.style.whiteSpace = 'nowrap'
        labelDiv.style.border = '1px solid #e5e7eb'
        labelDiv.textContent = String(box.label)
        boxLabelRefs.current[index] = labelDiv
        container.appendChild(labelDiv)
      } else {
        // No label for this box (either no permission or no label text)
        boxLabelRefs.current[index] = null
      }

      container.appendChild(div)
    })

    recalcBoxes()
  }, [highlightBoxes, entryPrice, stopLoss, symbol, currentUser?.show_label])

  const recalcBoxes = () => {
    const chart = chartRef.current
    const cs = candlestickSeriesRef.current
    const container = chartContainerRef.current

    if (!chart || !cs || !container) return

    highlightBoxes.forEach((box, index) => {
      const div = boxRefs.current[index]
      if (!div || !box) return

      if (
        typeof box.low !== 'number' ||
        isNaN(box.low) ||
        typeof box.high !== 'number' ||
        isNaN(box.high)
      ) {
        div.style.display = 'none'
        return
      }

      const rawY1 = cs.priceToCoordinate(box.high)
      const rawY2 = cs.priceToCoordinate(box.low)
      if (rawY1 === null || rawY1 === undefined || rawY2 === null || rawY2 === undefined) {
        div.style.display = 'none'
        return
      }

      const topMarginPx = container.offsetHeight * 0.0 
      const bottomMarginPx = container.offsetHeight * 0.0 
      const timeScaleHeightPx = 13  
      const plottingAreaTop = topMarginPx + 0
      const plottingAreaBottom = container.offsetHeight - bottomMarginPx - timeScaleHeightPx - 15
      const plottingAreaLeft = 0
      const plottingAreaRight = container.offsetWidth - 70
      const y1 = rawY1 as number
      const y2 = rawY2 as number

      let xStart = 0
      let xEnd = container.offsetWidth
      const ft = parseTimeInChartTimezone(box.fromTime, selectedTimezone)
      const tt = parseTimeInChartTimezone(box.toTime, selectedTimezone)
      const timeScale = chart.timeScale()
      const lastCandleTime = candlesRef.current.length > 0 
        ? candlesRef.current[candlesRef.current.length - 1].time 
        : null
      
      const getTimeCoord = (ts?: number): number | undefined => {
        if (!ts || candlesRef.current.length === 0) return undefined
        
        const directCoord = timeScale.timeToCoordinate(ts as Time)
        if (typeof directCoord === 'number' && !isNaN(directCoord)) {
          return directCoord
        }
        
        if (lastCandleTime && ts > lastCandleTime) {
          const lastCandleCoord = timeScale.timeToCoordinate(lastCandleTime)
          if (typeof lastCandleCoord === 'number' && candlesRef.current.length >= 2) {
            const lastCandle = candlesRef.current[candlesRef.current.length - 1]
            const prevCandle = candlesRef.current[candlesRef.current.length - 2]
            const timeDiff = lastCandle.time - prevCandle.time
            
            const lastCoord = timeScale.timeToCoordinate(lastCandle.time)
            const prevCoord = timeScale.timeToCoordinate(prevCandle.time)
            
            if (typeof lastCoord === 'number' && typeof prevCoord === 'number' && timeDiff > 0) {
              const pixelDiff = lastCoord - prevCoord
              const timeDiffToTarget = ts - lastCandleTime
              const pixelDiffToTarget = (timeDiffToTarget / timeDiff) * pixelDiff
              return lastCoord + pixelDiffToTarget
            }
          }
        }
        
        let nearest = candlesRef.current[0].time
        let minDiff = Math.abs(nearest - ts)
        for (let i = 1; i < candlesRef.current.length; i++) {
          const t0 = candlesRef.current[i].time
          const d = Math.abs(t0 - ts)
          if (d < minDiff) {
            minDiff = d
            nearest = t0
          }
        }
        const c = timeScale.timeToCoordinate(nearest)
        return typeof c === 'number' ? c : undefined
      }
      
      if (ft !== undefined && tt !== undefined) {
        const x1 = getTimeCoord(ft)
        const x2 = getTimeCoord(tt)
        
        if (x1 !== undefined && x2 !== undefined) {
          xStart = Math.min(x1, x2)
          xEnd = Math.max(x1, x2)
        }
      }

      const boxTop = Math.min(y1, y2)
      const boxBottom = Math.max(y1, y2)
      const boxLeft = xStart
      const boxRight = xEnd
      const boxHeight = boxBottom - boxTop
      const boxWidth = boxRight - boxLeft
      const clippedTop = Math.max(plottingAreaTop, boxTop)
      const clippedBottom = Math.min(plottingAreaBottom, boxBottom)
      const clippedLeft = Math.max(plottingAreaLeft, boxLeft)
      const clippedRight = Math.min(plottingAreaRight, boxRight)
      const finalTop = clippedTop
      const finalHeight = Math.max(0, clippedBottom - clippedTop)
      const finalLeft = clippedLeft
      const finalWidth = Math.max(0, clippedRight - clippedLeft)
      const color = box.color || '#22c55e'
      const opacity = typeof box.opacity === 'number' ? box.opacity : 0.18
      const borderColor = box.borderColor || color

      if (finalWidth <= 0 || finalHeight <= 0 ||
          clippedTop >= plottingAreaBottom || clippedBottom <= plottingAreaTop ||
          clippedLeft >= plottingAreaRight || clippedRight <= plottingAreaLeft) {
        div.style.display = 'none'
        const labelDiv = boxLabelRefs.current[index]
        if (labelDiv) {
          labelDiv.style.display = 'none'
        }
        return
      }

      div.style.display = 'block'
      div.style.left = `${finalLeft}px`
      div.style.top = `${finalTop}px`
      div.style.width = `${finalWidth}px`
      div.style.height = `${finalHeight}px`
      div.style.backgroundColor = color
      div.style.opacity = `${opacity}`
      div.style.borderTop = `1px solid ${borderColor}`
      div.style.borderBottom = `1px solid ${borderColor}`
      const labelDiv = boxLabelRefs.current[index]
      
      // Update label position if it exists (only created for users with permission)
      if (labelDiv) {
        labelDiv.style.display = 'block'
        labelDiv.style.visibility = 'visible'
        labelDiv.style.left = `${finalLeft + 6}px`
        labelDiv.style.top = `${finalTop + 6}px`
      }
    })
  }

  useEffect(() => {
    recalcBoxes()
    const chart = chartRef.current
    if (!chart) return
    const ts = chart.timeScale()
    const handler = () => {
      try {
        recalcBoxes()
      } catch {}
    }
    ts.subscribeVisibleTimeRangeChange(handler)
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(handler)
    }
  }, [highlightBoxes, symbol, interval, height, selectedTimezone, currentUser?.show_label])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement
      setIsFullscreen(isNowFullscreen)

      if (chartRef.current && chartContainerRef.current) {
        setTimeout(() => {
          if (chartRef.current && chartContainerRef.current) {
            if (isNowFullscreen) {
              chartRef.current.applyOptions({
                width: window.innerWidth,
                height: window.innerHeight
              })
            } else {
              const container = chartContainerRef.current
              container.style.width = ''
              container.style.height = ''
              container.style.position = ''
              container.style.top = ''
              container.style.left = ''
              container.style.zIndex = ''

              setTimeout(() => {
                if (chartRef.current && container) {
                  chartRef.current.applyOptions({
                    width: container.offsetWidth,
                    height: height
                  })
                  recalcBoxes()
                }
              }, 50)
              return
            }
            recalcBoxes()
          }
        }, 100)
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [height])

  const handleResetScale = () => {
    const chart = chartRef.current
    if (!chart) return

    if (initialTimeRangeRef.current) {
      chart.timeScale().setVisibleRange(initialTimeRangeRef.current)
    } else {
      chart.timeScale().fitContent()
    }
    chart.applyOptions({ timeScale: { rightOffset: 10 } })

    const rightPriceScale = chart.priceScale('right')
    if (rightPriceScale && typeof rightPriceScale.applyOptions === 'function') {
      rightPriceScale.applyOptions({ autoScale: true })
    }
  }

  const handleFullscreen = async () => {
    const container = chartContainerRef.current
    if (!container) return

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen()
        container.style.width = '100vw'
        container.style.height = '100vh'
        container.style.position = 'fixed'
        container.style.top = '0'
        container.style.left = '0'
        container.style.zIndex = '9999'
      } else {
        await document.exitFullscreen()
        container.style.width = ''
        container.style.height = ''
        container.style.position = ''
        container.style.top = ''
        container.style.left = ''
        container.style.zIndex = ''
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  return (
    <div
      ref={chartContainerRef}
      className='tradingview-light-chart position-relative'
      style={{height: height, width: '100%'}}
    >
      {loading && (
        <div
          className='position-absolute w-100 h-100 top-0 start-0 text-center bg-white opacity-90 d-flex flex-column justify-content-center align-items-center'
          style={{zIndex: 5}}
        >
          <div className='spinner-border text-primary mb-3' />
          <p className='mb-0 text-gray-600'>{intl.formatMessage({id: 'CHART.LOADING_CANDLES'})}</p>
        </div>
      )}
      {error && (
        <div
          className='position-absolute w-100 h-100 top-0 start-0 text-center bg-white opacity-95 d-flex flex-column justify-content-center align-items-center'
          style={{zIndex: 10}}
        >
          <div className='text-danger fw-bold mb-2'>{error}</div>
          <div className='small text-muted'>{intl.formatMessage({id: 'CHART.ERROR_NO_DATA'})}</div>
        </div>
      )}

      {!loading && !error && (
        <div
          className='position-absolute d-flex justify-content-center'
          style={{
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 15
          }}
        >
          <div className='d-flex gap-2'>
            <select
              value={selectedTimezone}
              onChange={(e) => setSelectedTimezone(e.target.value)}
              className='form-select form-select-sm'
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '12px',
                width: 'auto',
                minWidth: '120px',
              }}
              title={intl.formatMessage({id: 'CHART.TIMEZONE_SELECT_TITLE'})}
            >
              {TIMEZONE_VALUES.map((value) => (
                <option key={value} value={value}>
                  {intl.formatMessage({id: getTzLabelKey(value)})}
                </option>
              ))}
            </select>

            <button
              type='button'
              onClick={handleFullscreen}
              className='btn btn-sm btn-outline-secondary'
              style={{
                padding: '4px 4px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              title={isFullscreen ? intl.formatMessage({id: 'CHART.FULLSCREEN_EXIT'}) : intl.formatMessage({id: 'CHART.FULLSCREEN_ENTER'})}
            >
              <KTIcon iconName={isFullscreen ? 'cross-circle' : 'arrow-two-diagonals'} className='fs-1 pe-0' />
            </button>

            <button
              type='button'
              onClick={handleResetScale}
              className='btn btn-sm btn-outline-secondary'
              style={{
                padding: '4px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255, 255, 255)',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              title={intl.formatMessage({id: 'CHART.RESET_SCALE_TITLE'})}
            >
              <KTIcon iconName='arrows-loop' className='fs-1 pe-0' />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
